<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['edit', 'delete', 'pause', 'resume', 'stop']);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_PAUSED = 'paused';
const STATUS_STOPPED = 'stopped';
const STATUS_ACTIVE = 'active';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const isPaused = computed(() => props.status === STATUS_PAUSED);
const isStopped = computed(() => props.status === STATUS_STOPPED);
const isCompleted = computed(() => props.status === STATUS_COMPLETED);

const statusTextColor = computed(() => {
  if (isCompleted.value || isStopped.value) return 'text-n-slate-11';
  if (isPaused.value) return 'text-n-amber-11';
  if (isActive.value) return 'text-n-teal-11';
  return 'text-n-slate-12';
});

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  const statusMap = {
    [STATUS_COMPLETED]: 'Completed',
    [STATUS_PAUSED]: 'Paused',
    [STATUS_STOPPED]: 'Stopped',
    [STATUS_ACTIVE]: 'Active',
  };

  return statusMap[props.status] || 'Scheduled';
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
    <div class="flex items-center justify-end gap-2">
      <!-- Control buttons for one-off campaigns -->
      <template v-if="!isLiveChatType && !isCompleted && !isStopped">
        <Button
          v-if="isPaused"
          variant="faded"
          size="sm"
          color="teal"
          icon="i-lucide-play"
          title="Resume campaign"
          @click="emit('resume')"
        />
        <Button
          v-else-if="status === STATUS_ACTIVE"
          variant="faded"
          size="sm"
          color="amber"
          icon="i-lucide-pause"
          title="Pause campaign"
          @click="emit('pause')"
        />
        <Button
          variant="faded"
          size="sm"
          color="ruby"
          icon="i-lucide-square"
          title="Stop campaign"
          @click="emit('stop')"
        />
      </template>

      <!-- Edit button for live chat campaigns -->
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />

      <!-- Delete button -->
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
